import { useEffect, useState } from "react";
import type { StripePlanPrice, StripePricesResponse } from "../api/checkout";
import type { PaymentPlanOption } from "./types";

/** Zero-decimal currencies where 1 unit = 1 of the currency (not 1/100). */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga",
  "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf"
]);

/**
 * Convert Stripe unit amount (typically cents) to a human-readable price string.
 * Uses Intl.NumberFormat for proper currency symbol placement and formatting.
 */
const formatUnitAmount = (unitAmount: number, currency: string): string => {
  const code = currency.toLowerCase();
  const amount = ZERO_DECIMAL_CURRENCIES.has(code) ? unitAmount : unitAmount / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    // Fallback if Intl doesn't recognise the currency.
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
};

/** Map Stripe interval + intervalCount to approximate weeks. */
const intervalToWeeks = (interval: string, intervalCount: number): number => {
  switch (interval) {
    case "day":
      return (intervalCount * 1) / 7;
    case "week":
      return intervalCount;
    case "month":
      return intervalCount * 4.33;
    case "year":
      return intervalCount * 52;
    default:
      return intervalCount * 4.33;
  }
};

/** Map Stripe interval + intervalCount to a human-readable label. */
const intervalToLabel = (interval: string, intervalCount: number): string => {
  if (interval === "year") {
    return intervalCount === 1 ? "1 year" : `${intervalCount} years`;
  }
  if (interval === "month") {
    if (intervalCount === 1) return "1 month";
    if (intervalCount === 12) return "1 year";
    return `${intervalCount} months`;
  }
  if (interval === "week") {
    return intervalCount === 1 ? "1 week" : `${intervalCount} weeks`;
  }
  return `${intervalCount} ${interval}`;
};

const PLAN_ORDER = ["weekly", "monthly", "quarterly", "annual"] as const;
type StripePlanId = (typeof PLAN_ORDER)[number];

/** Which plan should be flagged as "most popular". */
const MOST_POPULAR_PLAN: StripePlanId = "annual";

const isStripePlanId = (planId: string): planId is StripePlanId =>
  PLAN_ORDER.includes(planId as StripePlanId);

const mapStripePriceToOption = (planId: StripePlanId, price: StripePlanPrice): PaymentPlanOption => ({
  id: planId,
  label: intervalToLabel(price.interval, price.intervalCount),
  savePercent: 0,
  weeksInPeriod: intervalToWeeks(price.interval, price.intervalCount),
  totalPrice: formatUnitAmount(price.unitAmount, price.currency),
  isMostPopular: planId === MOST_POPULAR_PLAN,
  currencyCode: price.currency.toUpperCase(),
  unitAmount: price.unitAmount,
  minorUnitDivisor: ZERO_DECIMAL_CURRENCIES.has(price.currency.toLowerCase()) ? 1 : 100
});

export const mapPricesResponseToPlans = (prices: StripePricesResponse): PaymentPlanOption[] => {
  if (prices.prices?.length) {
    const regular = prices.prices.flatMap((price) => {
      const planId = price.planId.toLowerCase();
      return isStripePlanId(planId) ? [mapStripePriceToOption(planId, price)] : [];
    });
    const offers = (prices.offers ?? []).flatMap(offer => {
      const planId = offer.price.planId;
      if (!isStripePlanId(planId) || offer.price.intervalCount !== 1 ||
          offer.price.interval !== (planId === "weekly" ? "week" : "year")) return [];
      return [{ ...mapStripePriceToOption(planId, offer.price), id: `${planId}_intro`,
        offer: { planId, promoCode: offer.promoCode, firstPeriodAmount: offer.firstPeriodAmount, discountDuration: offer.discountDuration } }];
    });
    return [...regular, ...offers];
  }

  return PLAN_ORDER.flatMap((planId) => {
    const price = prices[planId];
    return price ? [mapStripePriceToOption(planId, price)] : [];
  });
};

type LivePricesState = {
  /** Only prices verified by the checkout API may be purchased. */
  plans: PaymentPlanOption[];
  /** True while the initial fetch is in-flight. */
  isLoading: boolean;
  /** Non-null if the fetch failed. Checkout stays unavailable until retry succeeds. */
  error: string | null;
  retry: () => void;
};

/**
 * Fetches live Stripe prices on mount (once) and returns them mapped to
 * `PaymentPlanOption[]`. Never substitutes an unverified price on failure.
 *
 * Country detection is handled server-side via the Cloudflare `CF-IPCountry`
 * header, no client-side param needed.
 */
export const useLivePrices = (): LivePricesState => {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<LivePricesState, "retry">>({
    plans: [],
    isLoading: true,
    error: null
  });

  useEffect(() => {
    let cancelled = false;
    setState({ plans: [], isLoading: true, error: null });

    (async () => {
      try {
        const { fetchPrices } = await import("../api/checkout");
        const prices = await fetchPrices();
        const plans = mapPricesResponseToPlans(prices).filter(plan =>
          Number.isFinite(plan.unitAmount) && (plan.unitAmount ?? 0) > 0
        );
        if (!plans.length) throw new Error("No subscription plans are available.");
        if (!cancelled) {
          setState({
            plans,
            isLoading: false,
            error: null
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            plans: [],
            isLoading: false,
            error: err instanceof Error ? err.message : "Failed to fetch prices"
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { ...state, retry: () => setAttempt(value => value + 1) };
};
